/**
 * The exercises in today's workout, in today's order.
 *
 * This is a queue, not a form: it pre-loads what you're doing without
 * pre-creating any set rows. Empty placeholder sets would be indistinguishable
 * from real ones the moment they synced, and a workout you abandoned after two
 * exercises would report as a full session forever.
 *
 * Warm-ups deliberately don't count towards the prescription — three working
 * sets means three working sets, and counting the bar would mark an exercise
 * done before it started.
 *
 * The queue can be subtracted from as well as added to. A plan that prescribes
 * six exercises is a suggestion about a gym you might not be standing in: the
 * machine is taken, the shoulder hurts, or you swapped it for something else and
 * the original just sat there looking undone. Removing is per-session and never
 * touches the plan, exactly like `extras` in the other direction.
 *
 * `day` is optional, and that half matters as much as the plan half. A workout
 * started without a plan is still a workout with several exercises in it —
 * you walk in, do bench, then rows, then curls — and with no queue on screen
 * the only affordance was "Change", which swapped the panel and left no way
 * back to what you'd been doing. Same list, same drag, same remove; there is
 * just no prescription above it to compare against.
 */

import { useState } from 'react'

import type { Exercise, PlanDay, SetRow } from '../db'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { formatTarget } from '../lib/planTarget'
import { applyOrder } from '../lib/reorder'
import { countRounds } from '../lib/rounds'
import { visibleItems } from '../lib/sessionQueue'
import { DragList } from './DragList'
import { ExerciseInfoPanel, InfoButton, hasInfo } from './ExerciseInfo'
import type { DistancePref, Unit } from '../lib/units'

export function WorkoutQueue({
  day,
  sets,
  unit,
  distancePref,
  currentExerciseId,
  extras,
  removed,
  order,
  exercisesById,
  onPick,
  onAdd,
  onRemove,
  onRestore,
  onReorder,
}: {
  /** The prescription, when this workout came from one. Null for a freestyle
   *  workout — then the queue is nothing but what you've added today. */
  day: PlanDay | null
  sets: SetRow[]
  unit: Unit
  distancePref: DistancePref
  currentExerciseId: number | null
  /** Exercises added to today only. Not part of the plan, and never will be. */
  extras: number[]
  /** Exercises taken out of today only. Also not a change to the plan. */
  removed: number[]
  /** Today's running order, as exercise ids. Ids it doesn't mention go last. */
  order: number[]
  exercisesById: Map<number, Exercise>
  onPick: (exerciseId: number) => void
  onAdd: () => void
  /** Removes it from today, and deletes any sets logged against it. */
  onRemove: (exerciseId: number) => void
  onRestore: (exerciseId: number) => void
  /** A list dragged into a new order, as exercise ids. Per-session, not a plan
   *  edit — same rule as extras and removed. */
  onReorder: (ids: number[]) => void
}) {
  const overrides = useDistanceOverrides()
  // Which row is asking "are you sure". Only ever set for an exercise that has
  // sets logged against it — removing something you haven't touched needs no
  // ceremony, and putting it back is one tap away.
  const [confirming, setConfirming] = useState<number | null>(null)
  // Which row has its description expanded. One at a time, same as above: the
  // queue is the list you scan between sets, and two open paragraphs push the
  // thing you were looking for off the screen.
  const [info, setInfo] = useState<number | null>(null)

  const byExercise = new Map<number, SetRow[]>()
  for (const s of sets) {
    byExercise.set(s.exercise_id, [...(byExercise.get(s.exercise_id) ?? []), s])
  }

  const logged = new Map<number, number>()
  const allSets = new Map<number, number>()
  for (const [exerciseId, rows] of byExercise) {
    // Two counts, because they answer different questions. Progress through the
    // prescription ignores warm-ups; "what would removing this destroy" cannot.
    // Both count a left and a right as one set, and progress only counts it
    // once both sides are in — half a set does not satisfy a "3 sets". See
    // lib/rounds.
    allSets.set(exerciseId, countRounds(rows))
    logged.set(exerciseId, countRounds(rows, { working: true, complete: true }))
  }

  const isDone = (exerciseId: number | undefined, targetSets: number) =>
    exerciseId !== undefined && (logged.get(exerciseId) ?? 0) >= targetSets

  const items = day
    ? applyOrder(visibleItems(day.items, removed), (i) => i.exercise?.id, order)
    : []

  const doneCount = items.filter((i) => isDone(i.exercise?.id, i.target_sets)).length

  // Both helpers take only what they use. A PlanItem's exercise is a name and
  // an id, not a full Exercise row, and an extra's is — insisting on the wider
  // type here would just mean two copies of the same button.
  type Named = { id: number; name: string }

  /** The × on a row. A nested button is invalid, so rows are blocks, not flex. */
  const removeButton = (exercise: Named) => (
    <button
      className="secondary"
      aria-label={`Remove ${exercise.name} from this workout`}
      title="Remove from this workout"
      // Not a drag handle. Without this, holding the × grabs the row instead of
      // waiting for the tap it was reaching for.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={() => {
        if ((allSets.get(exercise.id) ?? 0) > 0) setConfirming(exercise.id)
        else onRemove(exercise.id)
      }}
      style={{
        marginTop: '.3rem',
        // Wide enough to hit with a thumb mid-set, which is the only time it
        // gets used. A 16px glyph is not a tap target.
        padding: '0 .8rem',
        minHeight: '2.4rem',
        fontSize: '1.1rem',
        lineHeight: 1,
        flex: '0 0 auto',
      }}
    >
      ×
    </button>
  )

  /**
   * The ? beside a row, and the paragraph it opens.
   *
   * Both take an id and look the full row up here, because what a plan item
   * carries is a name and an id — the description lives on the exercise, which
   * only the caller's cache has. A prescription the server couldn't resolve,
   * or an exercise nobody wrote a description for, gets no button at all.
   */
  const infoButton = (exerciseId: number) => {
    const full = exercisesById.get(exerciseId)
    if (!full || !hasInfo(full)) return null
    return (
      <InfoButton
        exercise={full}
        open={info === exerciseId}
        onToggle={() => setInfo((cur) => (cur === exerciseId ? null : exerciseId))}
      />
    )
  }

  const infoPanel = (exerciseId: number) => {
    const full = exercisesById.get(exerciseId)
    if (!full || info !== exerciseId) return null
    return <ExerciseInfoPanel exercise={full} />
  }

  const confirmStrip = (exercise: Named) => {
    const n = allSets.get(exercise.id) ?? 0
    return (
      <div
        style={{
          margin: '.3rem 0 .1rem',
          padding: '.45rem .6rem',
          background: 'var(--surface-2)',
          borderRadius: 'var(--radius)',
          fontSize: '.82rem',
        }}
      >
        <div className="muted">
          Remove {exercise.name} and delete the {n} set{n === 1 ? '' : 's'}{' '}
          logged against it?
        </div>
        <div style={{ display: 'flex', gap: '.4rem', marginTop: '.35rem' }}>
          <button
            style={{ marginTop: 0, padding: '.25rem .7rem', minHeight: 0, fontSize: '.82rem' }}
            onClick={() => {
              onRemove(exercise.id)
              setConfirming(null)
            }}
          >
            Remove
          </button>
          <button
            className="secondary"
            style={{ marginTop: 0, padding: '.25rem .7rem', minHeight: 0, fontSize: '.82rem' }}
            onClick={() => setConfirming(null)}
          >
            Keep it
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: '.6rem .75rem', margin: '.75rem 0' }}>
      {/* With a plan there is something to be measured against, so the header
          reports progress through it. Without one there is no target to be
          short of — counting exercises "done" against a number nobody set
          would invent a prescription out of thin air. */}
      <div className="row" style={{ marginBottom: '.2rem' }}>
        <strong style={{ fontSize: '.9rem' }}>{day ? day.name : 'Exercises'}</strong>
        <span className="muted" style={{ fontSize: '.8rem' }}>
          {day
            ? `${doneCount}/${items.length} done`
            : `${extras.length} exercise${extras.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <DragList
        items={items}
        idOf={(i) => i.exercise?.id}
        // DragList hands back the reordered rows; the session order is kept as
        // exercise ids, so map here rather than making it guess.
        onReorder={(next) => onReorder(
          next.map((i) => i.exercise?.id)
              .filter((id): id is number => id !== undefined),
        )}
        renderItem={(item, { dragging }) => {
          const ex = item.exercise
          const count = ex ? logged.get(ex.id) ?? 0 : 0
          const done = isDone(ex?.id, item.target_sets)
          const current = ex != null && ex.id === currentExerciseId

          return (
            <>
              <div style={{ display: 'flex', gap: '.4rem' }}>
                <button
                  className="secondary"
                  // An item whose exercise the server couldn't resolve is still
                  // worth showing — it's part of the prescription — but there's
                  // nothing to select, so it isn't a button you can press.
                  disabled={!ex}
                  onClick={() => ex && onPick(ex.id)}
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    marginTop: '.3rem',
                    // Amber until the prescription is met, green once it is. An
                    // item whose exercise the server couldn't resolve gets
                    // neither: there is nothing to be done or finished.
                    background: ex
                      ? done ? 'var(--set-done-fill)' : 'var(--set-todo-fill)'
                      : undefined,
                    borderColor: ex
                      ? done ? 'var(--set-done)' : 'var(--set-todo)'
                      : undefined,
                    // Ink, not the state colour. button.secondary paints its
                    // label with --accent, which is green — on an amber row that
                    // is both wrong and, at amber's lightness, unreadable.
                    color: ex ? 'var(--text)' : undefined,
                    // Done fades but stays legible and tappable: extra sets past
                    // the prescription are normal, and so is going back to fix a
                    // mistyped one.
                    opacity: done && !current && !dragging ? 0.55 : 1,
                    // Violet, not the accent. The accent is now the "finished"
                    // colour, so a green ring would read as a state rather than
                    // as "you are here" — and it would vanish on the green row it
                    // most needs to mark. --cal-today exists for exactly this in
                    // the calendar: a hue no status uses.
                    outline: current ? '2px solid var(--cal-today)' : undefined,
                  }}
                >
                  <span>
                    {item.superset_group && (
                      <span className="muted">{item.superset_group} · </span>
                    )}
                    {ex?.name ?? '—'}
                  </span>
                  <span className="muted" style={{ fontSize: '.82rem' }}>
                    {' · '}{formatTarget(
                      item,
                      // The plan payload's nested exercise is a {id, name} stub
                      // with no tracking on it — the cached row is the one that
                      // knows whether this is metres or miles.
                      unitFor(ex ? exercisesById.get(ex.id) : undefined,
                              { unit, pref: distancePref, overrides }))}
                    {' · '}{count}/{item.target_sets} logged
                  </span>
                  {/* The cue, at the moment it's useful: you're looking at this
                      list to decide what to do next. */}
                  {item.notes && (
                    <div className="muted" style={{ fontSize: '.78rem', marginTop: '.15rem' }}>
                      {item.notes}
                    </div>
                  )}
                </button>
                {ex && infoButton(ex.id)}
                {ex && removeButton(ex)}
              </div>
              {ex && infoPanel(ex.id)}
              {ex && confirming === ex.id && confirmStrip(ex)}
            </>
          )
        }}
      />

      {/* Anything you did today that the plan didn't ask for. Listed under a
          heading that says so, because the whole point is that it's visibly
          separate: adding squats today must not read as squats now being part
          of this plan day for ever.

          No plan, no heading: there is nothing above for these to be separate
          from, and "added today — not part of the plan" over the only list on
          screen would be naming a plan that doesn't exist. */}
      {extras.length > 0 && (
        <>
          {day && (
            <div className="muted" style={{ fontSize: '.78rem', marginTop: '.5rem' }}>
              Added today — not part of the plan
            </div>
          )}
          <DragList
            items={extras}
            idOf={(id) => id}
            onReorder={onReorder}   /* already exercise ids */
            renderItem={(id) => {
              const exercise = exercisesById.get(id)
              const count = logged.get(id) ?? 0
              return (
                <>
                  <div style={{ display: 'flex', gap: '.4rem' }}>
                    <button
                      className="secondary"
                      onClick={() => onPick(id)}
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        marginTop: '.3rem',
                        // --cal-today, not the accent, and for the reason
                        // spelled out on the prescribed rows above: the
                        // accent is the "finished" colour, so one card
                        // cannot use it for "you are here" in one list and
                        // for "done" in the other.
                        outline: id === currentExerciseId
                          ? '2px solid var(--cal-today)' : undefined,
                      }}
                    >
                      {exercise?.name ?? 'Exercise'}
                      <span className="muted" style={{ fontSize: '.82rem' }}>
                        {' · '}{count} set{count === 1 ? '' : 's'}
                      </span>
                    </button>
                    {infoButton(id)}
                    {exercise && removeButton(exercise)}
                  </div>
                  {infoPanel(id)}
                  {exercise && confirming === id && confirmStrip(exercise)}
                </>
              )
            }}
          />
        </>
      )}

      {/* Recoverable, and visibly so. A removal you can't see is a removal you
          can't undo when you mis-tap, and this list is the only record — the
          plan itself is untouched, by design. */}
      {removed.length > 0 && (
        <>
          <div className="muted" style={{ fontSize: '.78rem', marginTop: '.5rem' }}>
            Removed from today
          </div>
          <ul className="plain" style={{ margin: 0 }}>
            {removed.map((id) => (
              <li key={id}>
                <span className="muted" style={{ fontSize: '.85rem' }}>
                  {exercisesById.get(id)?.name ?? 'Exercise'}
                </span>
                <button
                  className="secondary"
                  onClick={() => onRestore(id)}
                  style={{
                    marginTop: 0, padding: '.2rem .6rem',
                    minHeight: 0, fontSize: '.78rem',
                  }}
                >
                  Put back
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <button
        className="secondary"
        style={{ width: '100%', marginTop: '.5rem', fontSize: '.85rem' }}
        onClick={onAdd}
      >
        {day ? 'Add an exercise to today' : 'Add an exercise'}
      </button>
    </div>
  )
}
